// Libs
import classNames from 'classnames/bind';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseTimeline, Loading } from '~/components/common';
import { ActivityDetail } from '~/components/module/activity';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
} from '~/utils/constants/common';
import { IActivity, IQueryParamActivity } from '~/utils/interface/activity';
import { getActivityList } from '~/thunks/activity/activityThunk';
import { BusinessTypeEnum, LoadingSizeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './EstimateActivity.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const EstimateActivity = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const { t } = useTranslation(['admin/estimate']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [activityList, setActivityList] = useState<IActivity[]>([]);
  const [hasMoreActivity, setHasMoreActivity] = useState<boolean>(false);
  const [currentPageActivity, setCurrentPageActivity] = useState<number>(
    BASE_PAGINATION_DEFAULT_CURRENT_PAGE
  );
  const [isShowActivityDetail, setIsShowActivityDetail] = useState<boolean>(false);
  const [activitySelected, setActivitySelected] = useState<IActivity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!estimateId) return;

    const params: IQueryParamActivity = {
      page: currentPageActivity,
      limit: DEFAULT_LIMIT_PAGE,
      targetId: estimateId,
      targetModel: BusinessTypeEnum.ESTIMATE,
    };

    handleGetActivityList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageActivity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleLoadMore = () => {
    setCurrentPageActivity((prev) => prev + DEFAULT_NUMBER_ONE);
  };

  const handleViewDetail = (activity: IActivity) => {
    setIsShowActivityDetail(true);
    setActivitySelected(activity);
  };

  const handleCloseActivityDetail = () => {
    setIsShowActivityDetail(false);
    setActivitySelected(null);
  };

  const handleGetActivityList = (params: IQueryParamActivity) => {
    if (currentPageActivity === BASE_PAGINATION_DEFAULT_CURRENT_PAGE) {
      setIsLoading(true);
    }

    dispatch(getActivityList(params))
      .unwrap()
      .then((res) => {
        if (!res?.data?.responses) return;

        const { responses } = res?.data;

        if (!responses || responses?.length === DEFAULT_NUMBER_ZERO) {
          setHasMoreActivity(false);

          return;
        }

        setActivityList((prev) => {
          const newData = responses?.filter(
            (data) => !prev?.some((oldActivity) => oldActivity?.id === data?.id)
          );

          const updateList = [...prev, ...newData];

          setHasMoreActivity(true);

          return updateList;
        });
      })
      .catch((error) => {
        setHasMoreActivity(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='estimateActivityPage' className={cx('container')}>
      {!isLoading &&
        (Array.isArray(activityList) && activityList?.length > DEFAULT_NUMBER_ZERO ? (
          <BaseTimeline
            data={activityList}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreActivity}
            onView={handleViewDetail}
            loadMoreSize={LoadingSizeEnum.MEDIUM}
          />
        ) : (
          <Typography component={'p'} className={cx('noDataAvailable')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        ))}

      <ActivityDetail
        isOpen={isShowActivityDetail}
        {...(activitySelected && { activity: activitySelected })}
        onClose={handleCloseActivityDetail}
      />

      {isLoading && <Loading />}
    </Box>
  );
};

export default EstimateActivity;
